import { useState, useCallback } from "react"

/**
 * Hook สำหรับจัดการ pagination
 */
export const usePagination = (initialPage = 1, initialPerPage = 10) => {
	const [currentPage, setCurrentPage] = useState(initialPage)
	const [perPage] = useState(initialPerPage)
	const [totalCount, setTotalCount] = useState(0)
	const [totalPages, setTotalPages] = useState(0)
	const [loading, setLoading] = useState(false)

	const updatePagination = useCallback((data) => {
		if (data.total !== undefined) setTotalCount(data.total)
		if (data.totalPages !== undefined) setTotalPages(data.totalPages)
		if (data.currentPage !== undefined) setCurrentPage(data.currentPage)
	}, [])

	const resetPagination = useCallback(() => {
		setCurrentPage(1)
		setTotalCount(0)
		setTotalPages(0)
	}, [])

	const goToPage = useCallback(
		(page) => {
			if (page >= 1 && page <= totalPages && !loading) {
				setCurrentPage(page)
			}
		},
		[totalPages, loading]
	)

	return {
		currentPage,
		perPage,
		totalCount,
		totalPages,
		loading,
		setLoading,
		updatePagination,
		resetPagination,
		goToPage,
	}
}

/**
 * Hook สำหรับจัดการ API calls แบบ paginated
 */
export const usePaginatedAPI = (apiFunction, dependencies = []) => {
	const pagination = usePagination()
	const [data, setData] = useState([])
	const [error, setError] = useState(null)

	const fetchData = useCallback(
		async (resourceId, page = pagination.currentPage, additionalParams = {}) => {
			pagination.setLoading(true)
			setError(null)

			try {
				const params = {
					page,
					limit: pagination.perPage,
					...additionalParams,
				}

				// ส่ง params ใน axios style
				const response = await apiFunction(resourceId, { params })

				// Handle different response structures
				let responseData, paginationData

				if (response.data || Array.isArray(response)) {
					// Old response structure
					responseData = response.data || response
					paginationData = {
						total: response.total || responseData.length,
						totalPages: Math.ceil((response.total || responseData.length) / pagination.perPage),
						currentPage: page,
					}
				} else {
					// New paginated response structure
					const dataKey = Object.keys(response).find((key) => Array.isArray(response[key]))
					responseData = response[dataKey] || []
					paginationData = {
						total: response.total,
						totalPages: response.totalPages,
						currentPage: response.currentPage || page,
					}
				}

				setData(responseData)
				pagination.updatePagination(paginationData)
			} catch (e) {
				console.error(`Error fetching data:`, e)
				setError(e)
				setData([])
				pagination.resetPagination()
			}

			pagination.setLoading(false)
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[apiFunction, pagination]
	)

	const goToPage = useCallback(
		(page) => {
			pagination.goToPage(page)
			return fetchData(null, page)
		},
		[fetchData, pagination]
	)

	return {
		data,
		error,
		fetchData,
		goToPage,
		...pagination,
	}
}

/**
 * Hook สำหรับจัดการ modal state
 */
export const useModal = (initialState = false) => {
	const [isOpen, setIsOpen] = useState(initialState)

	const openModal = useCallback(() => setIsOpen(true), [])
	const closeModal = useCallback(() => setIsOpen(false), [])
	const toggleModal = useCallback(() => setIsOpen((prev) => !prev), [])

	return {
		isOpen,
		openModal,
		closeModal,
		toggleModal,
	}
}

/**
 * Hook สำหรับจัดการ form state
 */
export const useForm = (initialValues = {}) => {
	const [values, setValues] = useState(initialValues)
	const [errors, setErrors] = useState({})
	const [loading, setLoading] = useState(false)

	const setValue = useCallback(
		(name, value) => {
			setValues((prev) => ({ ...prev, [name]: value }))
			// Clear error when value changes
			if (errors[name]) {
				setErrors((prev) => ({ ...prev, [name]: undefined }))
			}
		},
		[errors]
	)

	const setError = useCallback((name, error) => {
		setErrors((prev) => ({ ...prev, [name]: error }))
	}, [])

	const clearErrors = useCallback(() => {
		setErrors({})
	}, [])

	const reset = useCallback(
		(newValues = initialValues) => {
			setValues(newValues)
			setErrors({})
			setLoading(false)
		},
		[initialValues]
	)

	const handleChange = useCallback(
		(e) => {
			const { name, value, type, checked } = e.target
			setValue(name, type === "checkbox" ? checked : value)
		},
		[setValue]
	)

	return {
		values,
		errors,
		loading,
		setValue,
		setError,
		clearErrors,
		setLoading,
		reset,
		handleChange,
	}
}
